import { TranslationService } from '../../../services/translation.service';
import {
	FilterType,
	DatacenterFreeTextFilter,
	SuggestiveListFilter,
	DatacenterNumericFilter,
	FilterApiRequest,
	DropdownFilter,
	DatacenterBooleanFilter,
	GlobalSearchFilter,
} from '../../../models/datacenter-filters';
import { ORDER_SERVER_URL } from '../../../../config';

const GET_SCHEDULE_BUSINESS_ORDER_URL = `${ ORDER_SERVER_URL }/v3/scheduleBusinessOrders`;

type FilterTypeFn = () => FilterType;

export class ScheduleBusinessOrderFiltersConfiguration {
	constructor(
		private readonly translationService: TranslationService
	) {}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	SCHEDULE_BUSINESS_ORDER_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'steelgrade',
				() => new SuggestiveListFilter(
					'steelgrade',
					this.translationService.getTR('order-tracking.steelgrade'),
					this.translationService.getTR('order-tracking.steelgrade'),
					'steelgrade',
					'steelgradeDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'customerArticleReference',
				() => new DatacenterFreeTextFilter(
					'customerArticleReference',
					this.translationService
						.getTR('schedule-business-order-filter.customer-article-reference'),
					this.translationService
						.getTR('schedule-business-order-filter.customer-article-reference'),
					'customerArticleReference'
				)
			],
			[
				'width',
				() => new DatacenterNumericFilter(
					'width',
					this.translationService.getTR('schedule-business-order-filter.width'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'width'
				)
			],
			[
				'billTo',
				() => new SuggestiveListFilter(
					'billTo',
					this.translationService.getTR('order.bill.to'),
					this.translationService.getTR('order.bill.to'),
					'billTo',
					'billToDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'soldTo',
				() => new SuggestiveListFilter(
					'soldTo',
					this.translationService.getTR('order.customer'),
					this.translationService.getTR('order.customer'),
					'soldTo',
					'soldToDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'customerOrderNumber',
				() => new DatacenterFreeTextFilter(
					'customerOrderNumber',
					this.translationService
						.getTR('schedule-business-order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					'customerOrderNumber'
				)
			],
			[
				'supplierOrderNumber',
				() => new DatacenterFreeTextFilter(
					'supplierOrderNumber',
					this.translationService
						.getTR('schedule-business-order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					'supplierOrderNumber'
				)
			],
			[
				'endUser',
				() => new SuggestiveListFilter(
					'endUser',
					this.translationService.getTR('order.end.user'),
					this.translationService.getTR('order.end.user'),
					'endUser',
					'endUserDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'shipTo',
				() => new SuggestiveListFilter(
					'shipTo',
					this.translationService.getTR('order.ship.to'),
					this.translationService.getTR('order.ship.to'),
					'shipTo',
					'shipToDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'stockPartner',
				() => new SuggestiveListFilter(
					'stockPartner',
					this.translationService.getTR('schedule-business-order.stock-partner'),
					this.translationService.getTR('schedule-business-order.stock-partner'),
					'stockPartner',
					'stockPartnerDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('order.mill'),
					this.translationService.getTR('order.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'thickness',
				() => new DatacenterNumericFilter(
					'thickness',
					this.translationService.getTR('schedule-business-order-filter.thickness'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'thickness'
				)
			],
			[
				'productCategory',
				() => new DropdownFilter(
					'productCategory',
					this.translationService.getTR('order-tracking.product.category'),
					this.translationService.getTR('order-tracking.product.category'),
					'productCategory',
					'productCategoryDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500 })
				)
			],
			[
				'businessStatus',
				() => new DatacenterBooleanFilter(
					'businessStatus',
					this.translationService.getTR('schedule-business-order.sa-business-status'),
					[
						this.translationService.getTR('saBusinessStatus.start.up'),
						this.translationService.getTR('saBusinessStatus.running'),
						this.translationService.getTR('saBusinessStatus.running.out'),
						this.translationService.getTR('saBusinessStatus.closed'),
						this.translationService.getTR('saBusinessStatus.low.runner'),
						this.translationService.getTR('saBusinessStatus.high.runner'),
						this.translationService.getTR('saBusinessStatus.high.risk.internal'),
						this.translationService.getTR('saBusinessStatus.on.hold'),
						this.translationService.getTR('saBusinessStatus.high.risk.external'),
					],
					['businessStatus'],
					['01', '02', '03', '04', '05', '06', '07', '08', '09'],
				)
			],
			[
				'colorBottomSide',
				() => new DropdownFilter(
					'colorBottomSide',
					this.translationService.getTR('datacenter-filter.color-bottom-side'),
					this.translationService.getTR('datacenter-filter.color-bottom-side'),
					'colorBottomSide',
					'colorBottomSideDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500 })
				)
			],
			[
				'colorTopSide',
				() => new DropdownFilter(
					'colorTopSide',
					this.translationService.getTR('datacenter-filter.color-top-side'),
					this.translationService.getTR('datacenter-filter.color-top-side'),
					'colorTopSide',
					'colorTopSideDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500 })
				)
			],
			[
				'scheduleBusinessOrdersWithMaterial',
				() => new DatacenterBooleanFilter(
					'scheduleBusinessOrdersWithMaterial',
					this.translationService.getTR('filter.scheduleBusinessOrdersWithMaterial'),
					[
						this.translationService.getTR('order-tracking.in.production'),
						this.translationService.getTR('schedule-business-order.plant-stock'),
						this.translationService.getTR('schedule-business-order.in-transit-stock'),
						this.translationService.getTR('order-card.batches-consignment-stock'),
						this.translationService.getTR('schedule-business-order.stock-blocked'),
					],
					[
						'qtyInProduction',
						'qtyAvailableReadyToShip',
						'qtyAvailableInTransit',
						'qtyAvailableInConsignment',
						'qtyBlocked'
					],
				)
			],
			[
				'orderManagerDescription',
				() => new SuggestiveListFilter(
					'orderManager',
					this.translationService.getTR('order.responsible.order.manager'),
					this.translationService.getTR('order.responsible.order.manager'),
					'orderManager',
					'orderManagerDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true })
				)
			],
			[
				'length',
				() => new DatacenterNumericFilter(
					'length',
					this.translationService.getTR('batch-card.length'),
					this.translationService.getTR('filter.number.from'),
					this.translationService.getTR('filter.number.to'),
					'length'
				)
			],
			[
				'globalSearch',
				() => new GlobalSearchFilter(
					'globalSearch',
					this.translationService.getTR('global.search-filter'),
					this.translationService.getTR('global.search-filter'),
					[
						'customerArticleReference',
						'supplierOrderNumber',
						'customerOrderNumber'
					]
				)
			]
		]);

	// eslint-disable-next-line @typescript-eslint/naming-convention
	SCHEDULE_BUSINESS_ORDER_DEFAULT_DATACENTER_FILTERS = () =>
		new Map<string, FilterTypeFn>([
			[
				'mill',
				() => new SuggestiveListFilter(
					'mill',
					this.translationService.getTR('order.mill'),
					this.translationService.getTR('order.mill'),
					'mill',
					'millDescription',
					new FilterApiRequest(GET_SCHEDULE_BUSINESS_ORDER_URL, { limit: 1500, distinct: true }),
					[{}]
				)
			],
			[
				'customerOrderNumber',
				() => new DatacenterFreeTextFilter(
					'customerOrderNumber',
					this.translationService
						.getTR('schedule-business-order.customer.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					'customerOrderNumber',
					['']
				)
			],
			[
				'supplierOrderNumber',
				() => new DatacenterFreeTextFilter(
					'supplierOrderNumber',
					this.translationService
						.getTR('schedule-business-order.supplier.order.number'),
					this.translationService.getTR('filter.order-number.reference'),
					'supplierOrderNumber',
					['']
				)
			],
			[
				'customerArticleReference',
				() => new DatacenterFreeTextFilter(
					'customerArticleReference',
					this.translationService
						.getTR('schedule-business-order-filter.customer-article-reference'),
					this.translationService
						.getTR('schedule-business-order-filter.customer-article-reference'),
					'customerArticleReference',
					['']
				)
			],
			[
				'businessStatus',
				() => new DatacenterBooleanFilter(
					'businessStatus',
					this.translationService.getTR('schedule-business-order.sa-business-status'),
					[
						this.translationService.getTR('saBusinessStatus.start.up'),
						this.translationService.getTR('saBusinessStatus.running'),
						this.translationService.getTR('saBusinessStatus.running.out'),
						this.translationService.getTR('saBusinessStatus.closed'),
						this.translationService.getTR('saBusinessStatus.low.runner'),
						this.translationService.getTR('saBusinessStatus.high.runner'),
						this.translationService.getTR('saBusinessStatus.high.risk.internal'),
						this.translationService.getTR('saBusinessStatus.on.hold'),
						this.translationService.getTR('saBusinessStatus.high.risk.external'),
					],
					['businessStatus'],
					['01', '02', '03', '04', '05', '06', '07', '08', '09'],
					[
						[
							{ apiName: 'businessStatus', value: '01', selected: false },
							{ apiName: 'businessStatus', value: '02', selected: false },
							{ apiName: 'businessStatus', value: '03', selected: false },
							{ apiName: 'businessStatus', value: '04', selected: false },
							{ apiName: 'businessStatus', value: '05', selected: false },
							{ apiName: 'businessStatus', value: '06', selected: false },
							{ apiName: 'businessStatus', value: '07', selected: false },
							{ apiName: 'businessStatus', value: '08', selected: false },
							{ apiName: 'businessStatus', value: '09', selected: false },
						]
					]
				)
			]
		]);
}
